import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock, Target, TrendingUp, Calendar, DollarSign,
  CheckCircle, AlertCircle, Loader2, Save, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function BillableHours() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  // Goal settings form
  const [goalForm, setGoalForm] = useState({
    daily_goal: 6,
    weekly_goal: 30,
    monthly_goal: 120
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/billable-hours/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setGoalForm({
        daily_goal: response.data.goals.daily_goal,
        weekly_goal: response.data.goals.weekly_goal,
        monthly_goal: response.data.goals.monthly_goal
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching billable hours stats:', err);
      setError('Failed to load billable hours data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoals = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/billable-hours/goals`,
        goalForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchStats();
      setShowSettings(false);
    } catch (err) {
      console.error('Error saving goals:', err);
      alert('Failed to save goals');
    } finally {
      setSaving(false);
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'text-green-400';
    if (percent >= 70) return 'text-blue-400';
    if (percent >= 50) return 'text-amber-400';
    return 'text-red-400';
  };

  const getProgressBgColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 70) return 'bg-blue-500';
    if (percent >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getProgressGradient = (percent) => {
    if (percent >= 100) return 'from-green-500 to-emerald-500';
    if (percent >= 70) return 'from-blue-500 to-cyan-500';
    if (percent >= 50) return 'from-amber-500 to-yellow-500';
    return 'from-red-500 to-orange-500';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-400" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="bg-red-500/20 border border-red-500 text-red-400 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="text-blue-400" />
            Billable Hours
          </h1>
          <p className="text-slate-400 mt-1">
            Track your progress toward daily, weekly, and monthly goals
          </p>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Settings size={18} />
          Set Goals
        </button>
      </div>

      {/* Goals Settings Panel */}
      {showSettings && (
        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="text-blue-400" />
            Billable Hours Goals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Daily Goal (hours)
              </label>
              <input
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={goalForm.daily_goal}
                onChange={(e) => setGoalForm({ ...goalForm, daily_goal: parseFloat(e.target.value) || 0 })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Weekly Goal (hours)
              </label>
              <input
                type="number"
                min="0"
                max="168"
                step="1"
                value={goalForm.weekly_goal}
                onChange={(e) => setGoalForm({ ...goalForm, weekly_goal: parseFloat(e.target.value) || 0 })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Monthly Goal (hours)
              </label>
              <input
                type="number"
                min="0"
                max="744"
                step="1"
                value={goalForm.monthly_goal}
                onChange={(e) => setGoalForm({ ...goalForm, monthly_goal: parseFloat(e.target.value) || 0 })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4 gap-3">
            <button
              onClick={() => setShowSettings(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveGoals}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save Goals
            </button>
          </div>
        </div>
      )}

      {/* Progress Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Daily Progress */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-300">Today</h3>
            <span className={`text-2xl font-bold ${getProgressColor(stats.progress.daily_percent)}`}>
              {stats.progress.daily_percent}%
            </span>
          </div>
          <div className="mb-4">
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${getProgressGradient(stats.progress.daily_percent)} transition-all duration-500`}
                style={{ width: `${Math.min(stats.progress.daily_percent, 100)}%` }}
              />
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">
              <span className="text-white font-semibold">{stats.actual.today}</span> / {stats.goals.daily_goal} hours
            </span>
            {stats.progress.daily_percent >= 100 ? (
              <span className="text-green-400 flex items-center gap-1">
                <CheckCircle size={14} /> Goal met!
              </span>
            ) : (
              <span className="text-slate-500">
                {(stats.goals.daily_goal - stats.actual.today).toFixed(1)}h remaining
              </span>
            )}
          </div>
        </div>

        {/* Weekly Progress */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-300">This Week</h3>
            <span className={`text-2xl font-bold ${getProgressColor(stats.progress.weekly_percent)}`}>
              {stats.progress.weekly_percent}%
            </span>
          </div>
          <div className="mb-4">
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${getProgressGradient(stats.progress.weekly_percent)} transition-all duration-500`}
                style={{ width: `${Math.min(stats.progress.weekly_percent, 100)}%` }}
              />
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">
              <span className="text-white font-semibold">{stats.actual.this_week}</span> / {stats.goals.weekly_goal} hours
            </span>
            {stats.progress.weekly_percent >= 100 ? (
              <span className="text-green-400 flex items-center gap-1">
                <CheckCircle size={14} /> Goal met!
              </span>
            ) : (
              <span className="text-slate-500">
                {(stats.goals.weekly_goal - stats.actual.this_week).toFixed(1)}h remaining
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Week of {stats.period_info.week_start} - {stats.period_info.week_end}
          </div>
        </div>

        {/* Monthly Progress */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-300">{stats.period_info.month_name}</h3>
            <span className={`text-2xl font-bold ${getProgressColor(stats.progress.monthly_percent)}`}>
              {stats.progress.monthly_percent}%
            </span>
          </div>
          <div className="mb-4">
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${getProgressGradient(stats.progress.monthly_percent)} transition-all duration-500`}
                style={{ width: `${Math.min(stats.progress.monthly_percent, 100)}%` }}
              />
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">
              <span className="text-white font-semibold">{stats.actual.this_month}</span> / {stats.goals.monthly_goal} hours
            </span>
            {stats.progress.monthly_percent >= 100 ? (
              <span className="text-green-400 flex items-center gap-1">
                <CheckCircle size={14} /> Goal met!
              </span>
            ) : (
              <span className="text-slate-500">
                {(stats.goals.monthly_goal - stats.actual.this_month).toFixed(1)}h remaining
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {stats.period_info.working_days_remaining} working days remaining
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Hourly Rate */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <DollarSign size={16} />
            Hourly Rate
          </div>
          <p className="text-xl font-bold text-white">
            {stats.hourly_rate ? formatCurrency(stats.hourly_rate) : 'Not set'}
          </p>
        </div>

        {/* Potential Revenue */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <TrendingUp size={16} />
            Monthly Revenue Target
          </div>
          <p className="text-xl font-bold text-green-400">
            {stats.potential_revenue ? formatCurrency(stats.potential_revenue) : '-'}
          </p>
        </div>

        {/* Days Remaining */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <Calendar size={16} />
            Days Left in Month
          </div>
          <p className="text-xl font-bold text-white">
            {stats.period_info.days_remaining_in_month}
          </p>
        </div>

        {/* Required Daily Average */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <Target size={16} />
            Required Daily Avg
          </div>
          <p className="text-xl font-bold text-amber-400">
            {stats.period_info.working_days_remaining > 0
              ? ((stats.goals.monthly_goal - stats.actual.this_month) / stats.period_info.working_days_remaining).toFixed(1)
              : '0'
            }h
          </p>
          <p className="text-xs text-slate-500">to meet monthly goal</p>
        </div>
      </div>
    </div>
  );
}

export default BillableHours;
